import { z } from 'zod';
/**
 * WebSocket protocol — same events for CLI (Ink) + Web (Fastify WS + Next.js).
 * The harness loop emits these; both surfaces render them.
 * See Docs/25-WEB-ROADMAP Phase 0 exit: two surfaces import same lokma-shared.
 */
// ─── Client → Server ───────────────────────────────────────────────────────
/**
 * REQ-133: composer-level thinking budget. `off` (or absent) puts no
 * reasoning field on the upstream request; the rest map per adapter to
 * `reasoning_effort` (OpenAI-compatible) or `thinking.budget_tokens`
 * (Anthropic). Kept as one shared union so the client picker, the wire
 * schema and the adapters cannot drift apart.
 */
/**
 * REQ-139 — the reasoning ladder, mirroring Hermes' `EFFORT_LADDER`.
 *
 * One shared vocabulary, wider than any single wire accepts; each adapter
 * clamps the pick down to the nearest level its model supports instead of the
 * composer having to know per-model capabilities. `off` is not a wire level:
 * it means "send no reasoning field at all".
 */
export const REASONING_EFFORTS = ['off', 'minimal', 'low', 'medium', 'high', 'xhigh', 'max'];
/** The ladder without `off`, weakest → strongest (clamping order). */
export const REASONING_LADDER = ['minimal', 'low', 'medium', 'high', 'xhigh', 'max'];
/**
 * REQ-149 — session data rides the socket.
 *
 * The session list and the chat transcript used to arrive over REST (with a
 * 4 s sidebar poll for liveness). These wire rows are the payload of the
 * `sessions` / `transcript` / `transcript_append` frames, so the client can
 * request data over WS and let the server PUSH every row it appends. The
 * REST endpoints stay for CLI/curl callers (backward compatible).
 */
export const SESSION_ROLES = ['user', 'assistant', 'tool', 'thinking'];
/** One chat attachment on a transcript row (mirrors lokma-core `SessionAttachment`). */
export const SessionAttachmentSchema = z.object({
    path: z.string(),
    name: z.string(),
    mime: z.string(),
    size: z.number(),
});
/**
 * REQ-186 — one user-attached image riding a `prompt` frame.
 *
 * The composer downscales + JPEG-encodes in the browser and ships the raw
 * base64 payload (no `data:` prefix — `ProviderImage` shape verbatim), so the
 * server can hand the same bytes to the provider without a second decode.
 * Caps are hard: `MAX` entries per prompt and a per-image char budget that
 * keeps the whole frame under the WS `maxPayload`.
 */
export const PROMPT_MAX_IMAGES = 6;
/** ~1.5 MB binary per image (base64 is ~4/3 of the payload). */
export const PROMPT_IMAGE_BASE64_CHARS = 2_000_000;
export const PromptImageSchema = z.object({
    name: z.string().min(1).max(200),
    mime: z.string().min(1).max(80),
    dataBase64: z.string().min(1).max(PROMPT_IMAGE_BASE64_CHARS),
});
/**
 * REQ-187 — one user-attached TEXT file riding a `prompt` frame.
 *
 * The composer reads the file in the browser (text files directly; PDFs via
 * the server's extract endpoint) and ships the capped content, so the server
 * can hand the model a labeled `<file>` block without touching the user's
 * disk again. Caps are hard: `MAX` entries per prompt and a per-file content
 * budget (the composer appends a `[truncated …]` marker, hence the slack).
 */
export const PROMPT_MAX_FILES = 10;
/** Per-file inline content budget (chars) — the composer truncates beyond it. */
export const PROMPT_FILE_CHAR_CAP = 100_000;
/** Slack above the cap for the truncation marker the composer appends. */
export const PROMPT_FILE_CHAR_SLACK = 2_000;
export const PromptFileSchema = z.object({
    name: z.string().min(1).max(200),
    mime: z.string().min(1).max(120),
    size: z.number().int().min(0),
    content: z.string().max(PROMPT_FILE_CHAR_CAP + PROMPT_FILE_CHAR_SLACK),
});
/** One persisted transcript line (mirrors `SessionMessage` in lokma-core). */
export const TranscriptRowSchema = z.object({
    role: z.enum(SESSION_ROLES),
    content: z.string(),
    timestamp: z.string(),
    toolCallId: z.string().optional(),
    toolName: z.string().optional(),
    /** REQ-155: agent-sent files — images inline, other files become cards. */
    attachments: z.array(SessionAttachmentSchema).optional(),
    /**
     * REQ-186: images the user attached to this prompt — the socket is the
     * chat's primary source after a reload, so dropping them here would make
     * every sent image vanish on refresh (same trap as `attachments`).
     */
    images: z.array(PromptImageSchema).optional(),
    /**
     * REQ-187: files the user attached to this prompt — text content only
     * (images ride `images`, REQ-186). The server stores them on the user row
     * and assembles labeled `<file>` blocks for the model.
     */
    files: z.array(PromptFileSchema).optional(),
    /**
     * REQ-205: rows recorded by a CLIENT-side keyless run (`transcript_record`)
     * — the server spent no provider key on them. Stamped by the server, never
     * trusted from whoever sent the request; absent on rows the server's own
     * provider path wrote. Mirrored in lokma-core `SessionMessage` — keep both
     * in sync (the wire validator strips unknown keys).
     */
    origin: z.enum(['client-free']).optional(),
});
/** One sidebar row (mirrors `SessionSummary` in lokma-core). */
export const SessionRowSchema = z.object({
    id: z.string(),
    cwd: z.string(),
    title: z.string(),
    renamed: z.boolean(),
    model: z.string().nullable(),
    botId: z.string().nullable(),
    messageCount: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
    ownerId: z.string().nullable(),
    /**
     * REQ-121/REQ-149: live run flags ride the list rows, so a socket-fed
     * sidebar keeps the working-session badges the REST list carries.
     */
    running: z.boolean().optional(),
    queued: z.number().int().min(0).optional(),
});
/**
 * REQ-205 — one row a browser-side keyless run asks the server to persist.
 *
 * The run happened entirely in the client (no server key); this is only the
 * RECORD of it, written through the same `SessionStore.append` the server
 * loop uses. Roles are text-only: `tool` evidence is written by the loop
 * itself and must never be forgeable over this path. `timestamp` comes from
 * the client (it owns the real send/answer times) and is what makes a lost
 * ack retry idempotent — the same batch replayed posts identical stamps.
 */
export const CLIENT_FREE_RECORD_ROLES = ['user', 'assistant', 'thinking'];
/** Per-row content bound (chars) — well under the 16 MB frame cap, generous for long answers. */
export const CLIENT_FREE_ROW_CHAR_CAP = 400_000;
/** Row bound per record request (user + thinking + assistant + slack). */
export const CLIENT_FREE_RECORD_MAX_ROWS = 6;
export const ClientFreeRecordRowSchema = z.object({
    role: z.enum(CLIENT_FREE_RECORD_ROLES),
    content: z.string().min(1).max(CLIENT_FREE_ROW_CHAR_CAP),
    timestamp: z.string().min(1).max(40),
});
export const ClientMessageSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('prompt'),
        prompt: z.string(),
        sessionId: z.string().optional(),
        // Mid-session model override (persisted via PATCH /api/sessions/:id).
        model: z.string().optional(),
        // Workspace-relative `@file` mentions — the server reads these into context.
        contextPaths: z.array(z.string()).max(5).optional(),
        // Thinking budget for this prompt (REQ-133).
        reasoningEffort: z.enum(REASONING_EFFORTS).optional(),
        // REQ-186: attached images as real bytes — the server stores them on the
        // user row and the adapters emit them as content parts next to the text.
        images: z.array(PromptImageSchema).max(PROMPT_MAX_IMAGES).optional(),
        // REQ-187: attached text files (content read + capped in the browser) —
        // the server stores them on the user row and assembles `<file>` blocks
        // for the model.
        files: z.array(PromptFileSchema).max(PROMPT_MAX_FILES).optional(),
    }),
    z.object({ type: z.literal('abort'), sessionId: z.string() }),
    z.object({ type: z.literal('permission_response'), requestId: z.string(), decision: z.enum(['allow', 'deny', 'always']) }),
    z.object({ type: z.literal('ask_response'), requestId: z.string(), answer: z.string() }),
    // REQ-182: the open_project modal answers the agent's call — 'done' (the
    // user confirmed, or no UI was attached to ask) or 'cancelled' (the user
    // dismissed it). Keyed by the frame's actionId; resolves the pending
    // project gate so the tool result carries the outcome the user chose.
    z.object({
        type: z.literal('project_ack'),
        actionId: z.string().min(1).max(64),
        outcome: z.enum(['done', 'cancelled']),
    }),
    // Terminal pane (W3-10): stdin + resize + kill travel over the same
    // `/ws/:sessionId` socket; output comes back as `terminal/data|exit`.
    z.object({
        type: z.literal('terminal/input'),
        terminalId: z.string().min(1).max(64),
        data: z.string().max(64 * 1024),
    }),
    z.object({
        type: z.literal('terminal/resize'),
        terminalId: z.string().min(1).max(64),
        cols: z.number().int().min(1).max(500),
        rows: z.number().int().min(1).max(200),
    }),
    z.object({
        type: z.literal('terminal/kill'),
        terminalId: z.string().min(1).max(64),
    }),
    // REQ-149: session data over the socket. `sessions_list` asks for the
    // sidebar rows (the socket also becomes a live subscriber), and
    // `transcript_get` asks for one session's history. Answers arrive as the
    // `sessions` / `transcript` frames; growth is PUSHED as `transcript_append`.
    z.object({ type: z.literal('sessions_list') }),
    z.object({
        type: z.literal('transcript_get'),
        sessionId: z.string().min(1).max(128),
    }),
    // REQ-205 slice 3: a browser-side KEYLESS run (OmniRoute free pool, no API
    // key anywhere) finished — persist the rows it produced through the SAME
    // transcript store the server loop writes. `origin` names the run's source
    // (today only `client-free`); the server stamps it onto every appended row
    // so a reader can tell the server spent no provider key on them.
    z.object({
        type: z.literal('transcript_record'),
        sessionId: z.string().min(1).max(128),
        origin: z.literal('client-free'),
        rows: z.array(ClientFreeRecordRowSchema).min(1).max(CLIENT_FREE_RECORD_MAX_ROWS),
    }),
]);
// ─── Server → Client ───────────────────────────────────────────────────────
export const ServerMessageSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('text_delta'), delta: z.string(), sessionId: z.string() }),
    // REQ-050: live reasoning stream (Claude-Code style thinking block).
    z.object({ type: z.literal('thinking_delta'), delta: z.string(), sessionId: z.string() }),
    // REQ-077: auto-retry notice — stream died, waiting waitMs then trying
    // again (attempt of maxAttempts). Never persisted; toast/badge only.
    z.object({
        type: z.literal('retry_notice'),
        attempt: z.number().int().min(1),
        maxAttempts: z.number().int().min(1),
        waitMs: z.number().int().min(0),
        message: z.string().max(300),
        sessionId: z.string(),
    }),
    z.object({ type: z.literal('tool_start'), tool: z.string(), input: z.unknown(), callId: z.string(), sessionId: z.string() }),
    z.object({ type: z.literal('tool_result'), callId: z.string(), result: z.unknown(), isError: z.boolean().default(false), sessionId: z.string() }),
    z.object({ type: z.literal('permission_request'), requestId: z.string(), tool: z.string(), description: z.string(), sessionId: z.string() }),
    z.object({ type: z.literal('ask_user_question'), requestId: z.string(), question: z.string(), choices: z.array(z.string()).optional(), sessionId: z.string() }),
    z.object({
        type: z.literal('cost'),
        sessionId: z.string(),
        inputTokens: z.number(),
        outputTokens: z.number(),
        costUsd: z.number(),
        model: z.string(),
        /**
         * REQ-174: the thinking level this run forwarded to the adapter (`off`
         * when none). Rides the cost frame so the run meta line can show it —
         * optional, because engine-driven runs (claude-code) emit no level.
         */
        reasoningEffort: z.enum(REASONING_EFFORTS).optional(),
    }),
    z.object({ type: z.literal('agent_state'), agentId: z.string(), state: z.string(), sessionId: z.string().optional() }),
    /**
     * REQ-202 kapsam 5 — the loop console's live frame.
     *
     * The payload is deliberately just the CHANGED ids, never the loop records:
     * a frame that carried rows would need the loop schema on the wire (and a
     * migration every time a field is added), while an id list says "re-read
     * these", which the client already does through the same route the console
     * mounted with. An EMPTY list means "the catalog changed shape" (a create or
     * a delete) and asks for a full re-read.
     */
    z.object({ type: z.literal('loop'), loopIds: z.array(z.string().max(64)).max(200) }),
    // Terminal pane (W3-10): live process output + exit, scoped to the
    // spawning session so tabs in other sessions never see each other's bytes.
    z.object({
        type: z.literal('terminal/data'),
        terminalId: z.string().min(1).max(64),
        data: z.string(),
        sessionId: z.string(),
    }),
    z.object({
        type: z.literal('terminal/exit'),
        terminalId: z.string().min(1).max(64),
        exitCode: z.number().int().nullable(),
        signal: z.string().nullable(),
        sessionId: z.string(),
    }),
    z.object({ type: z.literal('done'), sessionId: z.string(), reason: z.enum(['complete', 'aborted', 'error']).default('complete') }),
    // REQ-057: the agent loop drives the Web UI surface (browser / terminal /
    // session panes) through the same socket. Tools run server-side AND emit
    // one of these so every connected client opens/focuses the matching pane —
    // the user sees exactly what the agent did, live.
    // REQ-180: `open_project` carries the registered project id + its cwd
    // beside the fresh session id (`targetSessionId`).
    z.object({
        type: z.literal('ui_action'),
        actionId: z.string().min(1).max(64),
        action: z.enum(['open_browser', 'open_terminal', 'open_session', 'send_to_session', 'open_project', 'open_loop']),
        url: z.string().max(2048).optional(),
        tabId: z.string().max(64).optional(),
        terminalId: z.string().max(64).optional(),
        targetSessionId: z.string().max(128).optional(),
        prompt: z.string().max(8000).optional(),
        projectId: z.string().max(64).optional(),
        cwd: z.string().max(500).optional(),
        // REQ-182: display name so the confirmation modal/toast needs no refetch.
        projectName: z.string().max(60).optional(),
        // REQ-201: the created loop's identity, so the console can badge the row
        // as agent-created without a refetch. A Zod object drops unknown keys, so
        // without these two lines the panel never learns which loop appeared.
        loopId: z.string().max(64).optional(),
        loopName: z.string().max(80).optional(),
        // REQ-193 slice 10 (Kapsam 5): the record's own agent-open stamp, so the
        // pane shows the "agent" badge on the FIRST paint. The tab list carries
        // the same field for later paints; a Zod object drops unknown keys, so
        // without this line the badge would never arrive over the wire at all.
        openedByAgentAt: z.string().max(40).optional(),
        sessionId: z.string(),
    }),
    // REQ-149: answers to `sessions_list` / `transcript_get` and the live
    // push for every row the server appends. `transcript` is a full snapshot
    // (open + reconnect catch-up), `transcript_append` is one new row — the
    // client never needs the 4 s REST poll to see growth.
    z.object({ type: z.literal('sessions'), sessions: z.array(SessionRowSchema) }),
    z.object({
        type: z.literal('transcript'),
        sessionId: z.string(),
        messages: z.array(TranscriptRowSchema),
    }),
    z.object({
        type: z.literal('transcript_append'),
        sessionId: z.string(),
        message: TranscriptRowSchema,
    }),
    // REQ-205 slice 3: ack for `transcript_record` — `count` rows were appended
    // (0 = the identical batch was already persisted, which is success on a
    // retry after a lost ack, never a failure).
    z.object({
        type: z.literal('transcript_recorded'),
        sessionId: z.string(),
        count: z.number().int().min(0),
    }),
    z.object({ type: z.literal('error'), message: z.string(), code: z.string().optional(), sessionId: z.string().optional() }),
]);
// ─── Helpers ───────────────────────────────────────────────────────────────
/** Type-safe JSON stringify for WS send. */
export function encodeServerMessage(msg) {
    return JSON.stringify(msg);
}
/** Parse + validate incoming client message. Returns null on invalid. */
export function decodeClientMessage(raw) {
    try {
        const parsed = JSON.parse(raw);
        return ClientMessageSchema.parse(parsed);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=ws.js.map